import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import {
  nest,
  hierarchy,
  treemap,
  treemapDice,
  treemapSquarify,
  scaleLinear,
  scaleBand,
  descending,
  max,
} from 'd3';

import Component from './Component';

const margin = (_, props) => ({ ...{ left: 100, right: 50, bottom: 20 }, ...props.margin })
const keyBy = (_, props) => props.keyBy;
const aggregateBy = (_, props) => props.aggregateBy;
const layout = (_, props) => props.layout;
const gutter = state => state.gutter || 0;
const rollupIndex = (_, props) => props.rollupIndex || 16;
const benchmarks = (_, props) => props.benchmarks || [];

const computeChart = (
  set,
  layout,
  keys = [],
  prefix,
  size,
  margin,
  valueScale,
  offset = [0, 0],
  benchmarkData,
) => {
  const nesting = keys.reduce((nest, key) => nest.key(d => d[key]), nest());
  const values = nesting.entries(set);
  const root = hierarchy({ key: null, values }, d => d.values);
  root.sum(d => d.weighting);
  root.eachAfter(d => {
    d.percentage = d.value / root.value;
  });

  const treemapLayout = treemap();
  treemapLayout.round(true);

  let benchmarks = [];

  if (layout === 'Bar chart') {
    const categoricalScale = scaleBand();
    categoricalScale
      .domain(root.children.filter(d => d.value).map(d => d.data.key))
      .rangeRound([0, size[1] - (margin.top || 0) - (margin.bottom || 0)])
      .padding(0.33);

    treemapLayout.tile(treemapDice);

    root.children.forEach(node => {
      const width = valueScale(node.value);
      const xOffset = margin.left;
      const yOffset =
        (margin.top || 0) + (node.value ? categoricalScale(node.data.key) : 0);

      node.eachAfter(node => (node.depth -= 1));
      treemapLayout.size([width, categoricalScale.bandwidth()])(node);
      node.eachAfter(node => {
        node.depth += 1;
        node.x0 += xOffset;
        node.x1 += xOffset;
        node.y0 += yOffset;
        node.y1 += yOffset;
        node.x2 = size[0] - margin.right;
      });
    });

    benchmarks = benchmarkData.map(b => 
      b.map(e => ({ 
        ...e,
        x: valueScale(e.weighting), 
        y0: categoricalScale(e.value.replace("_"," ")) - 0.2*categoricalScale.bandwidth(),
        y1: categoricalScale(e.value.replace("_"," ")) + 1.2*categoricalScale.bandwidth(),
      })).sort((a,b) => a.y0 - b.y0)
    )
  } else {
    treemapLayout.paddingInner(2);

    root.sort((a, b) => descending(a.value, b.value));
    treemapLayout.tile((parent, x0, y0, x1, y1) => {
      (parent.depth === 0 && keys.length > 1 ? treemapDice : treemapSquarify)(
        parent,
        x0,
        y0,
        x1,
        y1
      );
    });
    treemapLayout.size(size)(root);
  }

  const process = ({ data, value, percentage, x0, y0, x1, y1, x2 }) => ({
    key: data.key,
    side: prefix,
    value,
    percentage,
    data,
    x0: x0 + offset[0],
    y0: y0 + offset[1],
    x1: x1 + offset[0],
    y1: y1 + offset[1],
    x2: x2 + offset[0],
  });

  const allNodes = root.descendants();
  const nodes = allNodes.filter(({ height }) => height === 1).map(node => ({
    ...process(node),
    children: node.children.map(process),
  }));

  const groupNodes = keys.length > 1
    ? allNodes.filter(({ depth }) => depth === 1).map(process)
    : [];

  return { nodes, groupNodes, root, benchmarks };
};

const isVertical = createSelector(
  [(_, props) => props.width, layout],
  (width, layout) => width < 729
);

const size = createSelector(
  [(_, props) => props.width, gutter, isVertical, layout],
  (width, gutter, isVertical, layout) =>
    (isVertical 
      ? [width, 600] 
      : [width, (width - gutter) / 2])
);

const sideSize = createSelector(
  [size, gutter, isVertical],
  (size, gutter, isVertical) =>
    (isVertical
      ? [size[0], (size[1] - gutter) / 2]
      : [(size[0] - gutter) / 2, size[1]])
);

const groupingKeys = createSelector(
  [keyBy, aggregateBy],
  (keyBy, aggregateBy) =>
    (aggregateBy === keyBy ? [keyBy] : [aggregateBy, keyBy])
);

const swaps = createSelector([(_, props) => props.data], data =>
  data.map(swap => {
    const before = swap.before || { ...swap.after, weighting: 0 };
    const after = swap.after || { ...swap.before, weighting: 0 };
    before.willExit = after.weighting === 0;
    return {
      key: swap.key,
      before: { ...before, key: swap.key },
      after: { ...after, key: swap.key },
    };
  })
);

const numTicks = () => 6;
const valueScale = createSelector(
  [swaps, sideSize, margin, numTicks, benchmarks],
  (swaps, size, margin, numTicks, benchmarks) =>
    scaleLinear()
      .domain([
        0,
        Math.max(
          max(swaps, d => Math.max(d.before.weighting, d.after.weighting)), // data
          (!benchmarks.length ? 0 : max(benchmarks, d => max(d, e => e.weighting))) // benchmarks
        ),
      ])
      .nice(numTicks)
      .rangeRound([0, size[0] - (margin.left || 0) - (margin.right || 0)])
);

const sortedSwaps = createSelector(
  [swaps, layout],
  (swaps, layout) => layout !== 'Bar chart'
    ? swaps
    : [...swaps].sort((a, b) => descending(a.before.weighting, b.before.weighting))
);

const beforeSwaps = createSelector([sortedSwaps, layout, rollupIndex], 
  (swaps, layout, rollupIndex) => layout !== 'Bar chart'
    ? swaps.map(({ before }) => before)
    : swaps.map(({ before }) => before)
      .filter(o => !!o.weighting)
      .reduce((t,v,i) => i >= rollupIndex
        ? t.slice(0,rollupIndex).concat([{
            "value": "Other", 
            "weighting": v.weighting + ((t[rollupIndex] && t[rollupIndex].weighting) || 0)
          }])
        : t.concat([v]), [])
);
const before = createSelector(
  [beforeSwaps, layout, groupingKeys, sideSize, margin, valueScale, benchmarks],
  (swaps, layout, groupingKeys, size, margin, valueScale, benchmarks) =>
    computeChart(
      swaps,
      layout,
      groupingKeys,
      'before',
      size,
      margin,
      valueScale,
      [0,0],
      benchmarks
    )
);

const afterOffset = createSelector(
  [sideSize, gutter, isVertical],
  (sideSize, gutter, isVertical) =>
    (isVertical ? [0, sideSize[1] + gutter] : [sideSize[0] + gutter, 0])
);
const afterSwaps = createSelector([sortedSwaps, layout, rollupIndex], 
  (swaps, layout, rollupIndex) => layout !== 'Bar chart'
    ? swaps.map(({ after }) => after)
    : swaps.map(({ after }) => after)
      .filter(o => !!o.weighting)
      .reduce((t,v,i) => i >= rollupIndex
        ? t.slice(0,rollupIndex).concat([{
            "value": "Other", 
            "weighting": v.weighting + ((t[rollupIndex] && t[rollupIndex].weighting) || 0)
          }])
        : t.concat([v]), [])
);
const after = createSelector(
  [afterSwaps, layout, groupingKeys, sideSize, margin, valueScale, afterOffset, benchmarks],
  (swaps, layout, groupingKeys, size, margin, valueScale, afterOffset, benchmarks) =>
    computeChart(
      swaps,
      layout,
      groupingKeys,
      'after',
      size,
      margin,
      valueScale,
      afterOffset,
      benchmarks,
    )
);

const allNodes = createSelector([before, after], (before, after) => [
  ...before.nodes,
  ...after.nodes,
]);
const allGroupNodes = createSelector([before, after], (before, after) => [
  ...before.groupNodes,
  ...after.groupNodes,
]);
const allBenchmarks = createSelector([before,after], (before,after) => 
  ({ before: before.benchmarks, after: after.benchmarks }))

const mapStateToProps = createStructuredSelector({
  swaps,
  nodes: allNodes,
  groupNodes: allGroupNodes,
  benchmarks: allBenchmarks,
  size,
  sideSize,
  layout,
  aggregateBy,
  margin,
  valueScale,
  afterOffset,
  numTicks,

  // labelInside: createSelector(layout, layout => layout === 'Treemap'),
  trailingTransitionKeys: createSelector(layout, layout => {
    if (layout === 'Bar chart') return ['x0', 'x1'];
  }),
});

const mapDispatchToProps = dispatch => ({});

export default connect(mapStateToProps, mapDispatchToProps)(Component);



// WEBPACK FOOTER //
// ./src/Graphic/index.js